import type { ComponentProps, PropsWithChildren } from 'react'
import {
  KeyboardAvoidingView,
  type NativeScrollPoint,
  Platform,
  ScrollView,
  View,
  type ViewProps,
} from 'react-native'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'
import { useMediaQuery } from 'react-responsive'
import { Profile } from '@/src/common/components/Profile'
import { create } from 'zustand'
import { mesaColorNames } from '@/src/common/theme'
import { topTabBarHeight, useIsTabBarPositionedTop } from '@/src/common/components/TopTabBar'
import type { ExpoRouter } from 'expo-router'
import { MainWrapperProfileTestID } from '@/src/common/wrappers/MainWrapper/testIDs'
import { Section } from '@/src/common/components/Section'

export function useMinWidth1024() {
  return useMediaQuery({ minWidth: 1024 })
}

function useMinWidth640() {
  return useMediaQuery({ minWidth: 640 })
}

export function useMainWrapperPadding() {
  const minWidth1024 = useMinWidth1024()
  const minWidth640 = useMinWidth640()
  return minWidth1024 ? 40 : minWidth640 ? 24 : 16
}

export const useMainWrapperContentOffset = create<{
  contentOffsets: { [key in string]?: NativeScrollPoint }
  setContentOffset: (key: string, contentOffset: NativeScrollPoint) => void
}>((set, get) => ({
  contentOffsets: {},
  setContentOffset: (key, contentOffset) =>
    set({ contentOffsets: { ...get().contentOffsets, [key]: contentOffset } }),
}))

export const maxWidth = 1440

export const gap = 24

/**
 * screens on which {@link MainWrapper} is used. for now it's typed as every screen, but we can manually/automatically refine this later. in fact, if any when we do, we can ensure that any consumer of this information type-safely handles every single possible scenario / {@link MainScreen}
 *
 * importantly, by including {@link MainScreen}s in `TopBarBar`'s `handleExpansionForScreen` hook, you can have the user's scroll state dictate whether the `TopTabBar` is expanded/collapsed
 * @see {@link https://github.com/Acre-Rewards/acre-app/blob/dde021273cd7dfb06b2bd3a8f3cf312f06d57c8c/src/common/components/TopTabBar/index.tsx#L60-L66}
 */
export type MainScreen = Extract<ExpoRouter.__routes['href'], string>

/**
 * weird platform-specific inconsistencies
 * @see {@link https://mesamember.slack.com/archives/C07JK5U3H8S/p1742570989877749}
 */
const contentContainerStyleFlex = (
  Platform.OS === 'web' ? ({ flex: 1 } as const) : ({ flexShrink: 0 } as const)
) satisfies ViewProps['style']

export function MainWrapper({
  children,
  contentContainerStyle,
  footer,
  bodyProps,
  screen,
  showProfile,
  profileProps,
  scrollViewProps,
  insetTopProps,
  headerSectionProps,
  style,
  testID,
}: PropsWithChildren<{
  footer?: PropsWithChildren['children']
  contentContainerStyle?: ViewProps['style']
  bodyProps?: ViewProps
  /** @see {@link MainScreen} */
  screen: MainScreen
  showProfile?: boolean
  profileProps?: ComponentProps<typeof Profile>
  scrollViewProps?: ComponentProps<typeof ScrollView>
  insetTopProps?: ViewProps
  headerSectionProps?: ComponentProps<typeof Section>
}> &
  Pick<ComponentProps<typeof KeyboardAvoidingView>, 'testID' | 'style'>) {
  const safeAreaInsets = useSafeAreaInsets()
  const { setContentOffset } = useMainWrapperContentOffset()
  const isTabBarPositionedTop = useIsTabBarPositionedTop()
  const mainWrapperPadding = useMainWrapperPadding()

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={[{ flex: 1 }, style]}
      testID={testID}
    >
      <ScrollView
        {...scrollViewProps}
        contentContainerStyle={[
          { width: '100%', marginHorizontal: 'auto', justifyContent: 'space-between' },
          contentContainerStyleFlex,
          scrollViewProps?.contentContainerStyle,
        ]}
        onScroll={(e) => setContentOffset(screen, e.nativeEvent.contentOffset)}
        scrollEventThrottle={32}
        stickyHeaderIndices={[0]}
        style={[isTabBarPositionedTop && { paddingTop: topTabBarHeight }, scrollViewProps?.style]}
        keyboardShouldPersistTaps="always"
      >
        <View
          {...insetTopProps}
          style={[
            {
              height: safeAreaInsets.top,
              width: '100%',
              zIndex: 1,
              opacity: 0.8,
              top: 0,
              position: 'absolute',
              backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
              marginBottom: -safeAreaInsets.top,
            },
            insetTopProps?.style,
          ]}
        />
        <SafeAreaView
          style={[
            { paddingTop: mainWrapperPadding, flex: 1, justifyContent: 'space-between', gap },
            isTabBarPositionedTop && { paddingTop: gap },
            contentContainerStyle,
          ]}
        >
          <View
            {...bodyProps}
            style={[
              {
                paddingHorizontal: mainWrapperPadding,
                width: '100%',
                maxWidth,
                marginHorizontal: 'auto',
                gap,
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'stretch',
                alignContent: 'flex-start',
              },
              bodyProps?.style,
            ]}
          >
            {(typeof showProfile === 'boolean' ? showProfile : !isTabBarPositionedTop) && (
              <Profile
                testID={MainWrapperProfileTestID}
                {...profileProps}
                style={[
                  { flex: 1, flexBasis: '100%', width: '100%', padding: 0 },
                  profileProps?.style,
                ]}
                contentWrapperStyle={[{ padding: 16 }, profileProps?.contentWrapperStyle]}
              />
            )}
            {headerSectionProps ? <Section {...headerSectionProps} /> : null}
            {children}
          </View>
          {footer}
        </SafeAreaView>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}
